import axios from 'axios';

// const BASE_URL = 'http://localhost:3040';
// const BASE_URL = 'http://192.168.29.107:3040';
const BASE_URL = 'https://dct-app-quiz.onrender.com';
const API_URL = `${BASE_URL}/api`;

export { BASE_URL };

const api = axios.create({
  baseURL: API_URL,
  timeout: 600000 // 10 minutes timeout for AI operations
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and dispatch event for navigation
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Dispatch custom event instead of page reload
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'unauthorized' } }));
      return Promise.reject(error);
    } else if (error.response?.status === 403) {
      // Check if account was deactivated
      if (error.response?.data?.isDeactivated) {
        // Account deactivated - clear session and dispatch event
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        alert('Your account has been deactivated. Please contact an administrator.');
        // Dispatch custom event instead of page reload
        window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'deactivated' } }));
        return Promise.reject(error);
      } else {
        // Other forbidden errors
        console.error('Access denied:', error.response.data.message);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  updatePassword: (data) => api.put('/auth/update-password', data),
  getAllStudents: (page = 1, limit = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc') => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder
    });
    if (search) params.append('search', search);
    return api.get(`/auth/students?${params.toString()}`);
  },
  getStudent: (studentId) => api.get(`/auth/students/${studentId}`),
  getStudentPerformance: (studentId) => api.get(`/auth/students/${studentId}/performance`),
  getOnlineUsers: () => api.get('/auth/online-users'),
  getPendingUsers: () => api.get('/auth/pending-users'),
  approveUser: (userId) => api.patch(`/auth/approve-user/${userId}`),
  rejectUser: (userId) => api.delete(`/auth/reject-user/${userId}`),
  deleteUser: (userId) => api.delete(`/auth/delete-user/${userId}`),
  toggleUserStatus: (userId) => api.put(`/auth/toggle-user-status/${userId}`),
  resetStudentPassword: (userId) => api.post(`/auth/admin/reset-password/${userId}`),
  sendAccountDetails: (userId) => api.post(`/auth/admin/send-account-details/${userId}`),
  createUser: (data) => api.post('/auth/create-user', data),
};

export const quizAPI = {
  createQuiz: (formData) => api.post('/quiz/create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getQuizList: () => api.get('/quiz/list'),
  getMyQuizzes: (page = 1, limit = 10, filters = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (filters.search) params.append('search', filters.search);
    if (filters.group) params.append('group', filters.group);
    if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return api.get(`/quiz/my-quizzes?${params.toString()}`);
  },
  getQuizByCode: (code) => api.get(`/quiz/code/${code}`),
  getQuizById: (quizId) => api.get(`/quiz/by-id/${quizId}`),
  getQuizSubmissions: (quizId) => api.get(`/quiz/${quizId}/submissions`),
  deleteQuiz: (quizId) => api.delete(`/quiz/${quizId}`),
  updateQuiz: (quizId, data) => api.put(`/quiz/${quizId}`, data),
  deleteQuestion: (quizId, questionIndex) => api.delete(`/quiz/${quizId}/questions/${questionIndex}`),
  updateQuestionCorrectAnswer: (quizId, questionId, correctAnswer) => api.patch(`/quiz/${quizId}/questions/${questionId}/correct-answer`, { correctAnswer }),
  updateQuestionText: (quizId, questionId, questionText) => api.patch(`/quiz/${quizId}/questions/${questionId}/text`, { questionText }),
  updateQuestionOption: (quizId, questionId, optionIndex, optionText) => api.patch(`/quiz/${quizId}/questions/${questionId}/options/${optionIndex}`, { optionText }),
  shareResults: (quizId) => api.patch(`/quiz/${quizId}/share-results`),
  getQuizLeaderboard: (quizId) => api.get(`/quiz/${quizId}/leaderboard`),
  getOverallLeaderboard: () => api.get('/quiz/leaderboard/overall'),
  duplicateQuiz: (quizId, data) => api.post(`/quiz/${quizId}/duplicate`, data),
  getAllQuestions: (page = 1, limit = 15) => api.get(`/quiz/all-questions?page=${page}&limit=${limit}`),
  getQuestionBank: (params) => api.get(`/quiz/question-bank${params ? '?' + params : ''}`),
  generateCodeDistractors: (data) => api.post('/quiz/generate-code-distractors', data),
  getAllTags: () => api.get('/quiz/tags'),
  getQuizAnalytics: (quizId) => api.get(`/quiz/${quizId}/analytics`),
};

export const submissionAPI = {
  submitQuiz: (data) => api.post('/submission', data),
  getSubmission: (quizId) => api.get(`/submission/quiz/${quizId}`),
  getMySubmissions: (page = 1, limit = 10) => api.get(`/submission/my-submissions?page=${page}&limit=${limit}`),
  initiateRetake: (quizId) => api.post(`/submission/retake/${quizId}`),
  getMyAttempts: (quizId) => api.get(`/submission/my-attempts/${quizId}`),
  getStudentAttempts: (userId, quizId) => api.get(`/submission/student-attempts/${userId}/${quizId}`),
  getAllStudentAttempts: (userId, page = 1, limit = 20) => api.get(`/submission/student/${userId}/all-attempts?page=${page}&limit=${limit}`),
  flagQuestion: (data) => api.post('/submission/flag-question', data),
  getSubmissionFlags: (submissionId) => api.get(`/submission/submission/${submissionId}/flags`),
  getFlaggedQuestions: (params) => api.get(`/submission/flagged-questions${params ? '?' + params : ''}`),
  updateFlagStatus: (flagId, data) => api.patch(`/submission/flagged-questions/${flagId}`, data),
  deleteSubmission: (submissionId) => api.delete(`/submission/${submissionId}`),
};

export const groupAPI = {
  createGroup: (data) => api.post('/group/create', data),
  getMyGroups: () => api.get('/group/my-groups'),
  getStudentGroups: () => api.get('/group/student/my-groups'),
  getGroup: (groupId) => api.get(`/group/${groupId}`),
  addStudents: (groupId, studentIds) => api.post(`/group/${groupId}/add-students`, { studentIds }),
  removeStudent: (groupId, studentId) => api.delete(`/group/${groupId}/remove-student/${studentId}`),
  updateGroup: (groupId, data) => api.put(`/group/${groupId}`, data),
  updateGroupTags: (groupId, tagIds) => api.put(`/group/${groupId}/tags`, { tagIds }),
  updateGroupCategories: (groupId, categoryIds) => api.put(`/group/${groupId}/categories`, { categoryIds }),
  deleteGroup: (groupId) => api.delete(`/group/${groupId}`),
  getGroupLeaderboard: (groupId) => api.get(`/group/${groupId}/leaderboard`),
  getGroupQuizzes: (groupId) => api.get(`/group/${groupId}/quizzes`),
};

export const filesAPI = {
  listFiles: (folder = null) => api.get('/files/list', { params: { folder } }),
  deleteFile: (filename, folder = null) => api.delete(`/files/${filename}`, { params: { folder } }),
  deleteMultipleFiles: (filenames, folder = null) => api.post('/files/delete-multiple', { filenames, folder }),
};

export const questionAPI = {
  createQuestion: (data) => api.post('/question/create', data),
  getQuestions: (page = 1, limit = 20, filters = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (filters.search) params.append('search', filters.search);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.category) params.append('category', filters.category);
    if (filters.questionType) params.append('questionType', filters.questionType);
    return api.get(`/question/list?${params.toString()}`);
  },
  getQuestion: (id) => api.get(`/question/${id}`),
  updateQuestion: (id, data) => api.put(`/question/${id}`, data),
  deleteQuestion: (id) => api.delete(`/question/${id}`),
  bulkDelete: (questionIds) => api.post('/question/bulk-delete', { questionIds }),
  searchByTags: (tags, difficulty, limit) => api.post('/question/search-by-tags', { tags, difficulty, limit }),
  getQuestionFollowUps: (questionId) => api.get(`/question/${questionId}/follow-ups`),
  saveFollowUpToBank: (data) => api.post('/question/save-follow-up', data),
};

export const tagAPI = {
  getAllTags: () => api.get('/tag'),
  createTag: (name) => api.post('/tag', { name }),
  bulkCreateTags: (tags) => api.post('/tag/bulk', { tags }),
};

export const categoryAPI = {
  getAllCategories: () => api.get('/category'),
  getCategoryTree: () => api.get('/category/tree'),
  createCategory: (data) => api.post('/category', data),
  updateCategory: (id, data) => api.put(`/category/${id}`, data),
  deleteCategory: (id) => api.delete(`/category/${id}`),
};

export const interviewAPI = {
  createInterview: (data) => api.post('/interview/create', data),
  getInterviews: (page = 1, limit = 20, status = '', search = '', groupId = '', startDate = '', endDate = '') => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (groupId) params.append('groupId', groupId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`/interview/list?${params.toString()}`);
  },
  getInterview: (id) => api.get(`/interview/${id}`),
  getMyInterviews: (status = '') => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/interview/my/assigned${params}`);
  },
  startInterview: (id) => api.post(`/interview/${id}/start`),
  submitAnswer: (id, data) => api.post(`/interview/${id}/answer`, data),
  submitFollowUpAnswer: (id, data) => api.post(`/interview/${id}/answer`, {
    ...data,
    isFollowUp: true
  }),
  getPendingFollowUps: (id) => api.get(`/interview/${id}/pending-followups`),
  submitInterview: (id) => api.post(`/interview/${id}/submit`),
  getS3UploadUrl: (id, filename, contentType) => {
    return api.get(`/interview/${id}/s3-upload-url`, {
      params: { filename, contentType }
    });
  },
  uploadVideoToS3: async (presignedUrl, videoBlob, onUploadProgress) => {
    // Direct upload to S3 using presigned URL (no auth required)
    return axios.put(presignedUrl, videoBlob, {
      headers: {
        'Content-Type': videoBlob.type || 'video/webm'
      },
      onUploadProgress: onUploadProgress
    });
  },
  saveVideoUrl: (id, data) => api.post(`/interview/${id}/save-video-url`, data),
  uploadVideoDropbox: (id, formData, onUploadProgress) => {
    return api.post(`/interview/${id}/upload-video-dropbox`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onUploadProgress
    });
  },
  getVimeoUploadTicket: (id) => api.get(`/interview/${id}/vimeo-upload-ticket`),
  uploadVideo: (id, formData, onUploadProgress) => {
    return api.post(`/interview/${id}/upload-video`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onUploadProgress
    });
  },
  getResponses: (id) => api.get(`/interview/${id}/responses`),
  getMySubmissions: (interviewId) => api.get(`/interview/${interviewId}/my-submissions`),
  evaluateResponse: (responseId, data) => api.patch(`/interview/response/${responseId}/evaluate`, data),
  regenerateAI: (responseId) => api.post(`/interview/response/${responseId}/regenerate-ai`),
  deleteResponse: (responseId) => api.delete(`/interview/response/${responseId}`),
  getResponseVideoUrl: (responseId) => api.get(`/interview/response/${responseId}/video-url`),
  updateInterview: (id, data) => api.put(`/interview/${id}`, data),
  duplicateInterview: (id) => api.post(`/interview/${id}/duplicate`),
  deleteInterview: (id) => api.delete(`/interview/${id}`),
  askInterviewer: (id, data) => api.post(`/interview/${id}/ask-interviewer`, data),
  getLeaderboard: (id) => api.get(`/interview/${id}/leaderboard`),
  getStatistics: (id) => api.get(`/interview/${id}/statistics`),
  generateAnalytics: (id) => api.post(`/interview/${id}/generate-analytics`),
  getAnalyticsHistory: (id) => api.get(`/interview/${id}/analytics-history`),
  getAnalyticsReport: (id, analyticsId) => api.get(`/interview/${id}/analytics/${analyticsId}`),
  deleteAnalyticsReport: (id, analyticsId) => api.delete(`/interview/${id}/analytics/${analyticsId}`),
  getRecentCompleted: (limit = 5) => api.get(`/interview/dashboard/recent-completed?limit=${limit}`),
};

export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),

  // AI Model Management
  getAIModels: () => api.get('/settings/ai-models'),
  registerAIModel: (data) => api.post('/settings/ai-models', data),
  updateAIModel: (modelName, data) => api.put(`/settings/ai-models/${modelName}`, data),
  deleteAIModel: (modelName) => api.delete(`/settings/ai-models/${modelName}`),

  // AI Config Management
  updateAIConfig: (featureName, modelName) => api.put('/settings/ai-config', { featureName, modelName }),
  bulkUpdateAIConfig: (config) => api.put('/settings/ai-config/bulk', { config }),

  // Feature Management
  addFeature: (data) => api.post('/settings/features', data),
  updateFeature: (featureKey, data) => api.put(`/settings/features/${featureKey}`, data),
  deleteFeature: (featureKey) => api.delete(`/settings/features/${featureKey}`),
};

export const ttsAPI = {
  speak: (text, voiceId = null) => api.post('/tts/speak', { text, voiceId }, {
    responseType: 'blob'
  }),
};

export const flashcardAPI = {
  // Flashcard CRUD
  getAllFlashcards: () => api.get('/flashcard'),
  getMyFlashcards: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.category) queryParams.append('category', params.category);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return api.get(`/flashcard/my-flashcards?${queryParams.toString()}`);
  },
  getMyTags: () => api.get('/flashcard/my-tags'),
  getMyCategories: () => api.get('/flashcard/my-categories'),
  getFlashcardsByGroup: (groupId) => api.get(`/flashcard/by-group/${groupId}`),
  getFlashcard: (id) => api.get(`/flashcard/${id}`),
  createFlashcard: (data) => api.post('/flashcard', data),
  updateFlashcard: (id, data) => api.put(`/flashcard/${id}`, data),
  deleteFlashcard: (id) => api.delete(`/flashcard/${id}`),

  // Terms CRUD
  addTerm: (flashcardId, data) => api.post(`/flashcard/${flashcardId}/terms`, data),
  updateTerm: (flashcardId, termId, data) => api.put(`/flashcard/${flashcardId}/terms/${termId}`, data),
  deleteTerm: (flashcardId, termId) => api.delete(`/flashcard/${flashcardId}/terms/${termId}`),

  // AI Generation
  generateTerms: (flashcardId, data) => api.post(`/flashcard/${flashcardId}/generate-terms`, data),
  bulkAddTerms: (flashcardId, terms) => api.post(`/flashcard/${flashcardId}/terms/bulk`, { terms }),
};

export const courseAPI = {
  // Course CRUD
  createCourse: (data) => api.post('/course/create', data),
  getCourseList: (page = 1, limit = 20, filters = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.instructor) params.append('instructor', filters.instructor);
    return api.get(`/course/list?${params.toString()}`);
  },
  getMyCourses: () => api.get('/course/my-courses'),
  getEnrolledCourses: () => api.get('/course/enrolled'),
  getCourse: (id) => api.get(`/course/${id}`),
  updateCourse: (id, data) => api.put(`/course/${id}`, data),
  deleteCourse: (id, courseCode) => api.delete(`/course/${id}`, { data: { courseCode } }),

  // Enrollment
  enrollStudents: (courseId, studentIds, enrollmentSource = 'admin') =>
    api.post(`/course/${courseId}/enroll`, { studentIds, enrollmentSource }),
  enrollGroup: (courseId, groupId) =>
    api.post(`/course/${courseId}/enroll-group`, { groupId }),
  unenrollStudent: (courseId, studentId) =>
    api.delete(`/course/${courseId}/unenroll/${studentId}`),
  unenrollGroup: (courseId, groupId) =>
    api.delete(`/course/${courseId}/unenroll-group/${groupId}`),
  clearAllEnrollments: (courseId) =>
    api.delete(`/course/${courseId}/clear-enrollments`),
  getEnrolledStudents: (courseId) => api.get(`/course/${courseId}/students`),
  getEnrollments: (courseId) => api.get(`/course/${courseId}/enrollments`),

  // Progress
  getStudentProgress: (courseId, studentId) =>
    api.get(`/course/${courseId}/student/${studentId}/progress`),
  getAssetProgressMatrix: (courseId) =>
    api.get(`/course/${courseId}/asset-progress`),

  // Duplication
  duplicateCourse: (courseId, data) => api.post(`/course/${courseId}/duplicate`, data),
};

export const chapterAPI = {
  // Chapter CRUD
  createChapter: (data) => api.post('/chapter/create', data),
  getCourseChapters: (courseId) => api.get(`/chapter/course/${courseId}`),
  getChapter: (id) => api.get(`/chapter/${id}`),
  updateChapter: (id, data) => api.put(`/chapter/${id}`, data),
  deleteChapter: (id) => api.delete(`/chapter/${id}`),
  reorderChapters: (chapters) => api.post('/chapter/reorder', { chapters }),

  // Progress tracking (admin)
  getChaptersWithStats: (courseId) => api.get(`/chapter/course/${courseId}/with-stats`),
  getChapterProgressMatrix: (chapterId) => api.get(`/chapter/${chapterId}/progress-matrix`),
};

export const learningAssetAPI = {
  // Asset CRUD
  createAsset: (data) => api.post('/learning-asset/create', data),
  getCourseAssets: (courseId, chapterId = null) => {
    const params = chapterId ? `?chapterId=${chapterId}` : '';
    return api.get(`/learning-asset/course/${courseId}${params}`);
  },
  getAsset: (id) => api.get(`/learning-asset/${id}`),
  updateAsset: (id, data) => api.put(`/learning-asset/${id}`, data),
  deleteAsset: (id) => api.delete(`/learning-asset/${id}`),
  reorderAssets: (assets) => api.post('/learning-asset/reorder', { assets }),

  // File upload
  uploadFile: (formData, onUploadProgress) => {
    return api.post('/learning-asset/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
    });
  },

  // Progress
  markAssetComplete: (assetId, score = null, timeSpent = null) =>
    api.post(`/learning-asset/${assetId}/complete`, { score, timeSpent }),
  markAssetIncomplete: (assetId) =>
    api.post(`/learning-asset/${assetId}/incomplete`),
  updateVideoProgress: (assetId, watchedSeconds, totalDuration) =>
    api.post(`/learning-asset/${assetId}/video-progress`, { watchedSeconds, totalDuration }),

  // Admin: Mark complete/incomplete for specific student
  adminMarkAssetComplete: (assetId, studentId, score = null, timeSpent = null) =>
    api.post(`/learning-asset/${assetId}/admin-complete`, { studentId, score, timeSpent }),
  adminMarkAssetIncomplete: (assetId, studentId) =>
    api.post(`/learning-asset/${assetId}/admin-incomplete`, { studentId }),

  // AI Features
  generateStudyQuestions: (assetId, numQuestions = 5) =>
    api.post(`/learning-asset/${assetId}/generate-questions`, { numQuestions }),

  updateStudyQuestion: (assetId, questionIndex, questionData) =>
    api.put(`/learning-asset/${assetId}/study-questions/${questionIndex}`, questionData),

  deleteStudyQuestion: (assetId, questionIndex) =>
    api.delete(`/learning-asset/${assetId}/study-questions/${questionIndex}`),

  // Teaching Quality Analysis (Admin only)
  analyzeTeaching: (assetId) =>
    api.post(`/learning-asset/${assetId}/analyze-teaching`),

  getTeachingAnalysis: (assetId) =>
    api.get(`/learning-asset/${assetId}/teaching-analysis`),

  // Get all assets with filters
  getAllAssets: (params = '') => {
    const url = params ? `/learning-asset/all?${params}` : '/learning-asset/all';
    return api.get(url);
  }
};

export const aiAPI = {
  // Ask AI about course content
  ask: (data) => api.post('/ai/ask', data),
  // Get suggested questions for content
  suggestQuestions: (data) => api.post('/ai/suggest-questions', data),
  // Get all AI questions for a course (admin only)
  getCourseQuestions: (courseId) => api.get(`/ai/questions/${courseId}`),
  // Generate assignment description based on title
  generateAssignmentDescription: (title) => api.post('/ai/generate-assignment-description', { title })
};

export const assignmentAPI = {
  // Admin - check if assignment title exists
  checkTitle: (title) => api.post('/assignment/check-title', { title }),

  // Admin - generate assignments from transcript content
  generateFromTranscript: (data) => api.post('/assignment/generate-from-transcript', data),

  // Admin - create assignment
  createAssignment: (data) => api.post('/assignment/create', data),

  // Admin - update assignment
  updateAssignment: (assignmentId, data) => api.put(`/assignment/${assignmentId}`, data),

  // Admin - update pseudocode for a specific language
  updatePseudocode: (assignmentId, language, pseudocode) =>
    api.put(`/assignment/${assignmentId}/pseudocode/${language}`, { pseudocode }),

  // Admin - delete assignment
  deleteAssignment: (assignmentId) => api.delete(`/assignment/${assignmentId}`),

  // Admin - get all assignments with pagination, search, and sort
  getAllAssignments: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.complexity) queryParams.append('complexity', params.complexity);
    if (params.developerLevel) queryParams.append('developerLevel', params.developerLevel);
    if (params.language) queryParams.append('language', params.language);

    return api.get(`/assignment/all?${queryParams.toString()}`);
  },

  // Admin - get assignment by ID
  getAssignmentById: (id) => api.get(`/assignment/${id}`),

  // Student - access assignment by code
  getAssignmentByCode: (code) => api.get(`/assignment/code/${code}`),

  // Student - submit a step
  submitStep: (data) => api.post('/assignment/submit-step', data),

  // Student - run code without submitting
  runCode: (data) => api.post('/assignment/run-code', data),

  // Student - submit assignment as-is (even if incomplete)
  submitAssignmentAnyway: (data) => api.post('/assignment/submit-anyway', data),

  // Student - unlock base hint
  unlockHint: (data) => api.post('/assignment/unlock-hint', data),

  // Student - unlock follow-up hint
  unlockFollowUpHint: (data) => api.post('/assignment/unlock-follow-up-hint', data),

  // Student - submit quiz answers for a step
  submitQuiz: (data) => api.post('/assignment/submit-quiz', data),

  // Student - get my standalone assignments with pagination, search, and sort
  getMyAssignments: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return api.get(`/assignment/my-assignments?${queryParams.toString()}`);
  },

  // Student - get ALL my submissions (including course assignments)
  getAllMySubmissions: () => api.get('/assignment/my-submissions'),

  // Admin - get all submissions for an assignment
  getSubmissions: (assignmentId) => api.get(`/assignment/${assignmentId}/submissions`),

  // Admin - get all coding assignment submissions for a specific student
  getStudentSubmissions: (studentId) => api.get(`/assignment/student/${studentId}/submissions`),

  // Admin - mark submission as complete
  markSubmissionComplete: (submissionId, completed = true) =>
    api.put(`/assignment/submission/${submissionId}/mark-complete`, { completed }),

  // Admin - get detailed hint analysis
  getHintAnalysis: (submissionId) => api.get(`/assignment/submission/${submissionId}/hint-analysis`),

  // Admin - add assignment to course
  addToCourse: (courseId, data) => api.post(`/assignment/course/${courseId}/add-assignment`, data),

  // Admin - remove assignment from course
  removeFromCourse: (courseId, assignmentId) => api.delete(`/assignment/course/${courseId}/assignment/${assignmentId}`),

  // Admin - delete a submission
  deleteSubmission: (submissionId) => api.delete(`/assignment/submission/${submissionId}`),

  // Get assignments in a course
  getCourseAssignments: (courseId) => api.get(`/assignment/course/${courseId}/assignments`),

  // Admin - update quiz questions for a step
  updateQuizQuestions: (assignmentId, language, stepNumber, questions) =>
    api.put(`/assignment/${assignmentId}/quiz/${language}/${stepNumber}`, { questions }),

  // Admin - delete a quiz question
  deleteQuizQuestion: (assignmentId, language, stepNumber, questionIndex) =>
    api.delete(`/assignment/${assignmentId}/quiz/${language}/${stepNumber}/${questionIndex}`),

  // Admin - get AI questions asked during the assignment
  getAIQuestions: (assignmentId) => api.get(`/assignment/${assignmentId}/ai-questions`),

  // Admin - get groups that have access to this assignment via code repos
  getAssignmentGroups: (assignmentId) => api.get(`/assignment/${assignmentId}/groups`),

  // ===== LECTURE ASSIGNMENT ENDPOINTS =====

  // Admin - bulk save lecture assignments
  bulkSaveLectureAssignments: (data) => api.post('/assignment/lecture-assignments/bulk-save', data),

  // Get lecture assignments for a specific asset
  getLectureAssignmentsByAsset: (assetId) => api.get(`/assignment/lecture-assignments/asset/${assetId}`),

  // Get all lecture assignments with filters
  getAllLectureAssignments: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.append('type', params.type);
    if (params.complexity) queryParams.append('complexity', params.complexity);
    if (params.language) queryParams.append('language', params.language);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return api.get(`/assignment/lecture-assignments?${queryParams.toString()}`);
  },

  // Get single lecture assignment by ID
  getLectureAssignment: (id) => api.get(`/assignment/lecture-assignments/${id}`),

  // Admin - update lecture assignment
  updateLectureAssignment: (id, data) => api.put(`/assignment/lecture-assignments/${id}`, data),

  // Admin - delete lecture assignment
  deleteLectureAssignment: (id) => api.delete(`/assignment/lecture-assignments/${id}`),

  // Admin - generate problem statement for lecture assignment
  generateLectureProblemStatement: (id, length, additionalInput) =>
    api.post(`/assignment/lecture-assignments/${id}/generate-statement`, { length, additionalInput }),

  // Admin - save generated problem statement
  saveLectureProblemStatement: (id, length, content, additionalInput, setAsActive) =>
    api.post(`/assignment/lecture-assignments/${id}/save-statement`, { length, content, additionalInput, setAsActive }),

  // Get lecture assignments for a group/batch
  getLectureAssignmentsByGroup: (groupId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.type) queryParams.append('type', params.type);
    if (params.complexity) queryParams.append('complexity', params.complexity);
    if (params.language) queryParams.append('language', params.language);
    if (params.search) queryParams.append('search', params.search);
    return api.get(`/assignment/lecture-assignments/group/${groupId}?${queryParams.toString()}`);
  }
};

export const aiInstructionTemplateAPI = {
  // Get all templates (with optional filters)
  getTemplates: (params) => {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get(`/ai-instruction-template${queryString}`);
  },

  // Get only active templates (for dropdown)
  getActiveTemplates: () => api.get('/ai-instruction-template/active'),

  // Get single template by ID
  getTemplate: (id) => api.get(`/ai-instruction-template/${id}`),

  // Create new template
  createTemplate: (data) => api.post('/ai-instruction-template', data),

  // Update template
  updateTemplate: (id, data) => api.put(`/ai-instruction-template/${id}`, data),

  // Delete template
  deleteTemplate: (id) => api.delete(`/ai-instruction-template/${id}`),

  // Track template usage
  useTemplate: (id) => api.post(`/ai-instruction-template/${id}/use`)
};

// Code Repository API
export const codeRepoAPI = {
  // Admin endpoints
  getAllRepos: (params) => {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get(`/code-repo/admin/all${queryString}`);
  },

  getRepoById: (id) => api.get(`/code-repo/admin/${id}`),

  createRepo: (data) => api.post('/code-repo/admin', data),

  updateRepo: (id, data) => api.put(`/code-repo/admin/${id}`, data),

  deleteRepo: (id) => api.delete(`/code-repo/admin/${id}`),

  cloneRepo: (id) => api.post(`/code-repo/admin/${id}/clone`),

  // Assignment management
  addAssignments: (id, assignmentIds) =>
    api.post(`/code-repo/admin/${id}/assignments`, { assignmentIds }),

  removeAssignment: (id, assignmentId) =>
    api.delete(`/code-repo/admin/${id}/assignments/${assignmentId}`),

  reorderAssignments: (id, assignmentIds) =>
    api.put(`/code-repo/admin/${id}/assignments/reorder`, { assignmentIds }),

  // Group assignment
  assignToGroups: (id, groupIds) =>
    api.post(`/code-repo/admin/${id}/groups`, { groupIds }),

  removeFromGroup: (id, groupId) =>
    api.delete(`/code-repo/admin/${id}/groups/${groupId}`),

  // Student assignment
  assignToStudents: (id, studentIds) =>
    api.post(`/code-repo/admin/${id}/students`, { studentIds }),

  removeStudent: (id, studentId) =>
    api.delete(`/code-repo/admin/${id}/students/${studentId}`),

  getAssignedStudents: (id) => api.get(`/code-repo/admin/${id}/students`),

  // Progress tracking
  getStudentProgress: (repoId) =>
    api.get(`/code-repo/admin/${repoId}/progress/students`),

  getProgressMatrix: (repoId) =>
    api.get(`/code-repo/admin/${repoId}/progress/matrix`),

  getStudentRepoProgress: (repoId, studentId) =>
    api.get(`/code-repo/admin/${repoId}/student/${studentId}/progress`),

  // Student endpoints
  getMyRepos: () => api.get('/code-repo/my-repos'),

  getRepoByCode: (repoCode) => api.get(`/code-repo/repo/${repoCode}`),

  checkAccess: (repoCode) => api.get(`/code-repo/repo/${repoCode}/check-access`),

  getRecentCompleted: (limit = 5) => api.get(`/code-repo/dashboard/recent-completed?limit=${limit}`)
};

export const assetQuizAPI = {
  // Admin: Generate quiz for an asset
  generateQuiz: (data) => api.post('/asset-quiz/generate', data),

  // Get quiz for an asset (includes submission history)
  getAssetQuiz: (assetId) => api.get(`/asset-quiz/asset/${assetId}`),

  // Submit quiz attempt
  submitQuiz: (data) => api.post('/asset-quiz/submit', data),

  // Get submission details for review
  getSubmission: (submissionId) => api.get(`/asset-quiz/submission/${submissionId}`),

  // Admin: Get all submissions for student on specific asset
  getAssetSubmissions: (assetId, userId) =>
    api.get(`/asset-quiz/submissions/asset/${assetId}/user/${userId}`),

  // Admin: Delete quiz
  deleteQuiz: (quizId) => api.delete(`/asset-quiz/${quizId}`),

  // Admin: Update a specific question
  updateQuestion: (quizId, questionIndex, questionData) =>
    api.put(`/asset-quiz/${quizId}/question/${questionIndex}`, questionData),

  // Admin: Delete a specific question
  deleteQuestion: (quizId, questionIndex) =>
    api.delete(`/asset-quiz/${quizId}/question/${questionIndex}`),

  // Admin: Add a new question
  addQuestion: (quizId, questionData) =>
    api.post(`/asset-quiz/${quizId}/question`, questionData)
};

// Issue Tracking API
export const issuesAPI = {
  // Report an issue (authenticated users)
  reportIssue: (issueData) => api.post('/issues/report', issueData),

  // Get all issues (admin)
  getAllIssues: (params) => api.get('/issues', { params }),

  // Get single issue (admin)
  getIssue: (id) => api.get(`/issues/${id}`),

  // Update issue (admin)
  updateIssue: (id, data) => api.patch(`/issues/${id}`, data),

  // Delete issue (admin)
  deleteIssue: (id) => api.delete(`/issues/${id}`),

  // Get user's reported issues
  getMyIssues: () => api.get('/issues/my/reports'),

  // Get issue statistics (admin)
  getStatistics: () => api.get('/issues/stats/summary')
};

// Activity Tracking API
export const activityAPI = {
  // REMOVED: Log activity endpoint
  // logActivity: (data) => api.post('/activity/log', data),

  // REMOVED: End activity endpoint
  // endActivity: (data) => api.post('/activity/end', data),

  // End all active sessions (useful on logout)
  endAllSessions: () => api.post('/activity/end-all'),

  // Get my time tracking summary
  getMySummary: (startDate, endDate) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/activity/my-summary', { params });
  },

  // Get my daily time tracking
  getMyDaily: (days = 30) => api.get('/activity/my-daily', { params: { days } }),

  // Get my recent activity
  getMyRecent: (limit = 20) => api.get('/activity/my-recent', { params: { limit } }),

  // Admin: Get all users' time summary
  getAllUsersSummary: (startDate, endDate) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/activity/all-users-summary', { params });
  },

  // Admin: Get specific user's activity
  getUserActivity: (userId, params = {}) => api.get(`/activity/user/${userId}`, { params }),

  // Admin: Get feature usage statistics
  getFeatureStats: (params = {}) => api.get('/activity/feature-stats', { params })
};

// Fee Management API
export const feeManagementAPI = {
  // Create new fee management record (allows multiple fee records per student)
  createOrUpdateFee: (data) => {
    if (data.feeId) {
      // Update existing record
      const { feeId, ...payload } = data;
      return api.put(`/fee-management/${feeId}`, payload);
    } else {
      // Create new record
      return api.post('/fee-management', data);
    }
  },

  // Update existing fee management record
  updateFee: (feeId, data) => api.put(`/fee-management/${feeId}`, data),

  // Get all fee management records
  getAllFees: (params = {}) => api.get('/fee-management', { params }),

  // Get fee management for a specific user
  getUserFee: (userId) => api.get(`/fee-management/user/${userId}`),

  // Get fee statistics
  getStatistics: (params = {}) => api.get('/fee-management/statistics', { params }),

  // Get overdue fees
  getOverdueFees: () => api.get('/fee-management/overdue'),

  // Get pending reminders (upcoming due dates)
  getReminders: (daysAhead = 7) => api.get('/fee-management/reminders', { params: { daysAhead } }),

  // Get fee statistics and payments for a specific group
  getGroupFees: (groupId) => api.get(`/fee-management/group/${groupId}`),

  // Record a payment
  recordPayment: (feeManagementId, data) => {
    // If data contains a file, send as FormData
    if (data instanceof FormData) {
      return api.post(`/fee-management/${feeManagementId}/payment`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post(`/fee-management/${feeManagementId}/payment`, data);
  },

  // Get payment history
  getPaymentHistory: (feeManagementId) => api.get(`/fee-management/${feeManagementId}/payments`),

  // Delete fee management record
  deleteFee: (feeManagementId) => api.delete(`/fee-management/${feeManagementId}`),

  // Update reminder sent date
  updateReminder: (feeManagementId) => api.put(`/fee-management/${feeManagementId}/reminder`),

  // Send payment receipt via email
  sendReceipt: (paymentId) => api.post(`/fee-management/payment/${paymentId}/send-receipt`),

  // Send installment reminder via email
  sendInstallmentReminder: (feeManagementId, installmentNumber) => api.post(`/fee-management/${feeManagementId}/send-installment-reminder`, { installmentNumber }),

  // Update a payment transaction
  updatePayment: (paymentId, data) => {
    // If data contains a file, send as FormData
    if (data instanceof FormData) {
      return api.put(`/fee-management/payment/${paymentId}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/fee-management/payment/${paymentId}`, data);
  },

  // Delete a payment transaction
  deletePayment: (paymentId) => api.delete(`/fee-management/payment/${paymentId}`),

  // Get monthly income stats for a specific year
  getIncomeByMonth: (year) => api.get('/fee-management/income-by-month', { params: { year } })
};

// Bank Account API
export const bankAccountAPI = {
  // Create a new bank account
  createBankAccount: (data) => api.post('/bank-account', data),

  // Get all bank accounts (admin)
  getAllBankAccounts: (params = {}) => api.get('/bank-account', { params }),

  // Get all active bank accounts (available to all users)
  getActiveBankAccounts: () => api.get('/bank-account/active'),

  // Get a specific bank account
  getBankAccount: (id) => api.get(`/bank-account/${id}`),

  // Update a bank account
  updateBankAccount: (id, data) => api.put(`/bank-account/${id}`, data),

  // Delete a bank account
  deleteBankAccount: (id) => api.delete(`/bank-account/${id}`),

  // Get transactions for a specific bank account
  getBankAccountTransactions: (id, params = {}) => api.get(`/bank-account/${id}/transactions`, { params })
};

// Code Snippet API
export const codeSnippetAPI = {
  // CRUD Operations
  createSnippet: (data) => api.post('/code-snippet/create', data),
  getAllSnippets: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.programmingLanguage) queryParams.append('programmingLanguage', params.programmingLanguage);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.tags) queryParams.append('tags', params.tags);
    if (params.search) queryParams.append('search', params.search);
    if (params.isPublished !== undefined) queryParams.append('isPublished', params.isPublished);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    return api.get(`/code-snippet?${queryParams.toString()}`);
  },
  getSnippet: (id) => api.get(`/code-snippet/${id}`),
  getSnippetByCode: (code) => api.get(`/code-snippet/access/${code}`),
  updateSnippet: (id, data) => api.put(`/code-snippet/${id}`, data),
  deleteSnippet: (id) => api.delete(`/code-snippet/${id}`),

  // AI Operations
  regenerateExplanations: (id) => api.post(`/code-snippet/${id}/regenerate-explanations`),
  generateDescription: (data) => api.post('/code-snippet/generate-description', data),
  explainCode: (data) => api.post('/code-snippet/explain-code', data),

  // Statistics
  getStatistics: () => api.get('/code-snippet/stats/overview')
};

export const enquiryAPI = {
  lookup: (params) => api.get('/enquiries/lookup', { params }),
  create: (data) => api.post('/enquiries', data),
  list: (params) => api.get('/enquiries', { params }),
  get: (id) => api.get(`/enquiries/${id}`),
  updateCounseling: (id, data) => api.patch(`/enquiries/${id}/counseling`, data),
  updateStatus: (id, data) => api.patch(`/enquiries/${id}/status`, data),
  assign: (id, counselorId) => api.patch(`/enquiries/${id}/assign`, { counselorId }),
  remove: (id) => api.delete(`/enquiries/${id}`),
  bulk: (data) => api.patch('/enquiries/bulk', data),
  getAssignableStaff: () => api.get('/enquiries/assignable-staff'),
  getStats: () => api.get('/enquiries/stats'),
};
